import React, { useEffect, useState, useContext } from "react";
import { eventTarget } from "@cornerstonejs/core";
import ModalMain from "../../../components/Modal/ModalMain";
import ModalHeader from "../../../components/Modal/ModalHeader";
import ModalFooter from "../../../components/Modal/ModalFooter";
import themeContext from "../../../context/theme/ThemeContext";
import {
  CALIBRATION_REQUEST_EVENT,
  applyCalibration,
  removeCalibrationLine,
} from "../helpers/CalibrationService";

/**
 * MedDream-style "CALIBRATION" dialog. Mounted once in the viewer; it listens
 * for CALIBRATION_REQUEST_EVENT (fired after a Calibration line is drawn) and
 * asks the user the true length of that line. On Apply it sets a user
 * calibration so every measurement on the image reads mm. The transient
 * calibration line is removed on Apply or Cancel.
 */
const CalibrationModal = () => {
  const { getThemeColor } = useContext(themeContext);
  const [data, setData] = useState(null); // { annotationUID, imageId, worldDistance }
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const onRequest = (e) => {
      setData(e.detail);
      setValue("");
      setError("");
    };
    eventTarget.addEventListener(CALIBRATION_REQUEST_EVENT, onRequest);
    return () =>
      eventTarget.removeEventListener(CALIBRATION_REQUEST_EVENT, onRequest);
  }, []);

  const close = () => {
    if (data) removeCalibrationLine(data.annotationUID);
    setData(null);
    setValue("");
    setError("");
  };

  const handleApply = () => {
    const mm = parseFloat(value);
    if (!isFinite(mm) || mm <= 0) {
      setError("Enter a valid distance in mm.");
      return;
    }
    const ok = applyCalibration(data.imageId, data.worldDistance, mm);
    if (!ok) {
      setError("Could not apply calibration.");
      return;
    }
    removeCalibrationLine(data.annotationUID);
    setData(null);
    setValue("");
    setError("");
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") handleApply();
  };

  if (!data) return null;

  const modalDetails = {
    width: "360px",
    header: (
      <ModalHeader
        updatedModal={true}
        showPatientDetails={false}
        heading="Calibration"
        handleCancel={close}
      />
    ),
    body: (
      <div style={{ padding: "20px", color: getThemeColor("primary-color") }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            whiteSpace: "nowrap",
          }}
          onKeyDown={onKeyDown}
        >
          <span>Enter new distance</span>
          <input
            type="number"
            min="0"
            step="any"
            autoFocus
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError("");
            }}
            style={{
              flex: 1,
              minWidth: 0,
              padding: "6px 8px",
              borderRadius: "4px",
              border: "1px solid #555",
              background: "#2b2b2b",
              color: "#fff",
            }}
          />
          <span>mm</span>
        </div>
        {error && (
          <div style={{ color: "#ff6b6b", fontSize: "12px", marginTop: "8px" }}>
            {error}
          </div>
        )}
      </div>
    ),
    footer: (
      <ModalFooter
        DefaultButtonList={[{ title: "Close", onclick: close }]}
        PrimaryButtonList={[{ title: "Apply", onclick: handleApply }]}
      />
    ),
  };

  return <ModalMain showModal={true} modalDetails={modalDetails} />;
};

export default CalibrationModal;
